import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/admin';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import { formatCurrency, Currency } from '../utils/currencyUtils';
import {
  UserGroupIcon,
  ChartBarIcon,
  TrophyIcon,
  XCircleIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SparklesIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface TeamMemberMetrics {
  newLeads: { count: number; value: number };
  wonLeads: { count: number; value: number };
  lostLeads: { count: number; value: number };
  activePipeline: { count: number; value: number; weightedValue: number };
  conversionRate: number;
  avgDealSize: number;
  stageBreakdown?: Array<{ stage: string; count: number; value: number }>;
}

interface TeamMember {
  userId: number;
  name: string;
  email: string;
  role: string;
  metrics: TeamMemberMetrics;
}

const TeamView: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const currency: Currency = 'INR';
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const memberDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const canAccessTeamView = !!user && ['superadmin', 'admin', 'leadership'].includes(user.role);
  const { data: teamData, isLoading, error } = useQuery({
    queryKey: ['team-performance', period, currency],
    queryFn: () => adminApi.getTeamPerformance(period, currency),
    enabled: canAccessTeamView,
  });

  // Debug logging
  useEffect(() => {
    if (teamData) {
      console.log('Team Data:', teamData);
      console.log('Team Members:', teamData.teamMembers);
    }
    if (error) {
      console.error('Team Performance Error:', error);
    }
  }, [teamData, error]);

  // Calculate dropdown position and handle click outside
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          right: window.innerWidth - rect.right - window.scrollX,
        });
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        memberDropdownRef.current &&
        !memberDropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setIsMemberDropdownOpen(false);
      }
    };

    if (isMemberDropdownOpen) {
      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMemberDropdownOpen]);

  if (!canAccessTeamView) {
    return (
      <PremiumLayout title="Team View">
        <div className="premium-card p-6 text-center">
          <p className="text-red-600 font-medium">Access Denied</p>
          <p className="text-gray-600 mt-2">Team Performance is available to Super Admin, Admin, and Leadership only.</p>
        </div>
      </PremiumLayout>
    );
  }

  if (isLoading) {
    return (
      <PremiumLayout title="Team Performance">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-slate-800"></div>
        </div>
      </PremiumLayout>
    );
  }

  const allTeamMembers: TeamMember[] = (teamData?.teamMembers || []) as TeamMember[];
  
  // Filter by selected members
  const teamMembers = selectedMemberIds.length > 0
    ? allTeamMembers.filter(m => selectedMemberIds.includes(m.userId))
    : allTeamMembers;

  // Calculate totals from filtered members
  const teamTotals = teamMembers.length > 0 ? {
    newLeads: {
      count: teamMembers.reduce((sum, m) => sum + (m.metrics?.newLeads?.count || 0), 0),
      value: teamMembers.reduce((sum, m) => sum + (m.metrics?.newLeads?.value || 0), 0),
    },
    wonLeads: {
      count: teamMembers.reduce((sum, m) => sum + (m.metrics?.wonLeads?.count || 0), 0),
      value: teamMembers.reduce((sum, m) => sum + (m.metrics?.wonLeads?.value || 0), 0),
    },
    lostLeads: {
      count: teamMembers.reduce((sum, m) => sum + (m.metrics?.lostLeads?.count || 0), 0),
      value: teamMembers.reduce((sum, m) => sum + (m.metrics?.lostLeads?.value || 0), 0),
    },
    activePipeline: {
      count: teamMembers.reduce((sum, m) => sum + (m.metrics?.activePipeline?.count || 0), 0),
      value: teamMembers.reduce((sum, m) => sum + (m.metrics?.activePipeline?.value || 0), 0),
      weightedValue: teamMembers.reduce((sum, m) => sum + (m.metrics?.activePipeline?.weightedValue || 0), 0),
    },
  } : {
    newLeads: { count: 0, value: 0 },
    wonLeads: { count: 0, value: 0 },
    lostLeads: { count: 0, value: 0 },
    activePipeline: { count: 0, value: 0, weightedValue: 0 },
  };

  // Calculate rankings with null safety
  const rankedByWon = [...teamMembers].sort((a, b) => (b.metrics?.wonLeads?.value || 0) - (a.metrics?.wonLeads?.value || 0));
  const rankedByPipeline = [...teamMembers].sort((a, b) => (b.metrics?.activePipeline?.value || 0) - (a.metrics?.activePipeline?.value || 0));
  const rankedByConversion = [...teamMembers].sort((a, b) => (b.metrics?.conversionRate || 0) - (a.metrics?.conversionRate || 0));

  // Generate AI insights
  const generateInsights = () => {
    const insights = [];
    
    if (teamMembers.length === 0) {
      return ['No team members found. Add team members to see performance metrics.'];
    }

    // Top performer
    const topPerformer = rankedByWon[0];
    if (topPerformer && topPerformer.metrics?.wonLeads?.value > 0) {
      insights.push(
        `🏆 ${topPerformer.name} is the top performer with ${formatCurrency(topPerformer.metrics.wonLeads.value, currency)} in won deals this ${period}.`
      );
    }

    // Conversion rate insights
    if (teamMembers.length > 0) {
      const avgConversion = teamMembers.reduce((sum: number, m: TeamMember) => sum + (m.metrics?.conversionRate || 0), 0) / teamMembers.length;
      const bestConverter = rankedByConversion[0];
      if (bestConverter && bestConverter.metrics?.conversionRate && avgConversion > 0 && bestConverter.metrics.conversionRate > avgConversion * 1.2) {
        insights.push(
          `📈 ${bestConverter.name} has an exceptional conversion rate of ${bestConverter.metrics.conversionRate.toFixed(1)}%, well above the team average of ${avgConversion.toFixed(1)}%.`
        );
      }
    }

    // Pipeline insights
    const totalPipeline = teamTotals.activePipeline.value;
    const weightedPipeline = teamTotals.activePipeline.weightedValue;
    if (weightedPipeline > 0) {
      const pipelineEfficiency = (weightedPipeline / totalPipeline) * 100;
      insights.push(
        `💼 Team pipeline value: ${formatCurrency(totalPipeline, currency)} with a weighted forecast of ${formatCurrency(weightedPipeline, currency)} (${pipelineEfficiency.toFixed(1)}% probability-weighted).`
      );
    }

    // Growth insights
    const totalNew = teamTotals.newLeads.count;
    const totalWon = teamTotals.wonLeads.count;
    if (totalNew > 0 && totalWon > 0) {
      const winRate = (totalWon / totalNew) * 100;
      insights.push(
        `📊 Team generated ${totalNew} new leads and closed ${totalWon} deals (${winRate.toFixed(1)}% win rate) this ${period}.`
      );
    }

    // Warning insights
    const lowPerformers = teamMembers.filter(m => m.metrics.wonLeads.count === 0 && m.metrics.activePipeline.count > 0);
    if (lowPerformers.length > 0) {
      insights.push(
        `⚠️ ${lowPerformers.length} team member(s) have active pipeline but no wins this ${period}. Consider coaching or support.`
      );
    }

    return insights.length > 0 ? insights : ['No significant insights available for this period.'];
  };

  const insights = generateInsights();

  // Filter members based on search
  const filteredMembers = allTeamMembers.filter(member =>
    member.name.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(memberSearchTerm.toLowerCase())
  );

  // Toggle member selection
  const toggleMemberSelection = (memberId: number) => {
    setSelectedMemberIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Get selected members
  const selectedMembers = allTeamMembers.filter(m => selectedMemberIds.includes(m.userId));

  return (
    <PremiumLayout
      title="Team Performance"
      subtitle={`${period.charAt(0).toUpperCase() + period.slice(1)} view • ${teamMembers.length} team members`}
    >
      <div className="space-y-6">
        {/* Error State */}
        {error && (
          <div className="premium-card p-6 bg-red-50 border-red-200">
            <p className="text-red-800 font-medium mb-2">Error loading team performance</p>
            <p className="text-red-600 text-sm">{error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && allTeamMembers.length === 0 && (
          <div className="premium-card p-6 text-center">
            <UserGroupIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium mb-2">No team members found</p>
            <p className="text-gray-500 text-sm">
              Team members need to have leads assigned to them to appear in the team view.
            </p>
          </div>
        )}

        {/* Filters and Team Selection */}
        {allTeamMembers.length > 0 && (
          <div className="premium-card p-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <FunnelIcon className="h-5 w-5 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">Filters:</span>
                </div>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}
                  className="input-premium"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <UserGroupIcon className="h-5 w-5 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">Select members to evaluate:</span>
                </div>
                <div className="flex items-center space-x-2 flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSelectedMemberIds([]);
                      setIsMemberDropdownOpen(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedMemberIds.length === 0
                        ? 'bg-indigo-600 text-white shadow-md hover:bg-indigo-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    All ({allTeamMembers.length})
                  </button>

                  <div className="relative">
                    <button
                      ref={dropdownButtonRef}
                      onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center space-x-2 shadow-sm min-w-[200px] justify-between"
                    >
                      <span>
                        {selectedMemberIds.length === 0
                          ? 'Select team members...'
                          : `${selectedMemberIds.length} selected`}
                      </span>
                      <ChevronDownIcon
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          isMemberDropdownOpen ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Dropdown Menu - Rendered via Portal */}
                    {isMemberDropdownOpen && createPortal(
                      <div
                        ref={memberDropdownRef}
                        className="fixed w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-[99999] max-h-96 overflow-hidden flex flex-col"
                        style={{
                          top: `${dropdownPosition.top}px`,
                          right: `${dropdownPosition.right}px`,
                        }}
                      >
                        {/* Search Bar */}
                        <div className="p-3 border-b border-gray-200 bg-gray-50">
                          <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search members..."
                              value={memberSearchTerm}
                              onChange={(e) => setMemberSearchTerm(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>

                        {/* Member List */}
                        <div className="overflow-y-auto max-h-64">
                          {filteredMembers.length === 0 ? (
                            <div className="p-4 text-center text-sm text-gray-500">
                              No members found
                            </div>
                          ) : (
                            <div className="p-2">
                              {filteredMembers.map((member) => {
                                const isSelected = selectedMemberIds.includes(member.userId);
                                return (
                                  <div
                                    key={member.userId}
                                    onClick={() => toggleMemberSelection(member.userId)}
                                    className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                      isSelected
                                        ? 'bg-indigo-50 hover:bg-indigo-100'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div
                                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                        isSelected
                                          ? 'bg-indigo-600 border-indigo-600'
                                          : 'border-gray-300'
                                      }`}
                                    >
                                      {isSelected && (
                                        <CheckIcon className="h-3 w-3 text-white" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 truncate">
                                        {member.name}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        {member.email}
                                      </div>
                                    </div>
                                    <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                      {member.role}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Footer Actions */}
                        {selectedMemberIds.length > 0 && (
                          <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              {selectedMemberIds.length} selected
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMemberIds([]);
                              }}
                              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              Clear All
                            </button>
                          </div>
                        )}
                      </div>
                    , document.body)}
                  </div>

                  {/* Selected Members Chips */}
                  {selectedMemberIds.length > 0 && selectedMemberIds.length <= 3 && (
                    <div className="flex items-center space-x-2 flex-wrap gap-2">
                      {selectedMembers.map((member) => (
                        <div
                          key={member.userId}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium"
                        >
                          <span>{member.name}</span>
                          <button
                            onClick={() => toggleMemberSelection(member.userId)}
                            className="ml-1 hover:text-indigo-900"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show count if more than 3 selected */}
                  {selectedMemberIds.length > 3 && (
                    <div className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium">
                      {selectedMemberIds.length} members selected
                    </div>
                  )}
                </div>
              </div>
            </div>
            {selectedMemberIds.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing {selectedMemberIds.length} of {allTeamMembers.length} team members
                </p>
              </div>
            )}
          </div>
        )}

        {/* Team Totals Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="premium-card p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-blue-100 rounded-lg">
                <PlusIcon className="h-6 w-6 text-blue-600" />
              </div>
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">New Business</h3>
            <p className="text-2xl font-bold text-gray-900">{teamTotals.newLeads.count}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formatCurrency(teamTotals.newLeads.value, currency)}
            </p>
          </div>

          <div className="premium-card p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrophyIcon className="h-6 w-6 text-green-600" />
              </div>
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Won Deals</h3>
            <p className="text-2xl font-bold text-gray-900">{teamTotals.wonLeads.count}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formatCurrency(teamTotals.wonLeads.value, currency)}
            </p>
          </div>

          <div className="premium-card p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Lost Deals</h3>
            <p className="text-2xl font-bold text-gray-900">{teamTotals.lostLeads.count}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formatCurrency(teamTotals.lostLeads.value, currency)}
            </p>
          </div>

          <div className="premium-card p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <ArrowTrendingUpIcon className="h-5 w-5 text-indigo-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Active Pipeline</h3>
            <p className="text-2xl font-bold text-gray-900">{teamTotals.activePipeline.count}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formatCurrency(teamTotals.activePipeline.value, currency)}
            </p>
            <p className="text-xs text-indigo-600 mt-1 font-medium">
              Weighted: {formatCurrency(teamTotals.activePipeline.weightedValue, currency)}
            </p>
          </div>
        </div>

        {/* AI Insights */}
        <div className="premium-card p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <div className="flex items-center space-x-2 mb-4">
            <SparklesIcon className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">AI Insights</h2>
          </div>
          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-white/80 rounded-lg border border-indigo-100">
                <div className="flex-shrink-0 w-2 h-2 bg-indigo-500 rounded-full mt-2"></div>
                <p className="text-sm text-gray-700 flex-1">{insight}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Evaluate team members: comparison table */}
        {teamMembers.length > 0 && (
        <div className="premium-card overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <UserGroupIcon className="h-6 w-6 mr-2" />
              Evaluate team members
              {selectedMemberIds.length > 0 ? (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({teamMembers.length} selected)
                </span>
              ) : (
                <span className="ml-2 text-sm font-normal text-gray-500">(all team)</span>
              )}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Compare performance by new leads, won/lost, pipeline, conversion rate and average deal size. Use the filter above to select specific members.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    New Leads
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Won
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Lost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Pipeline
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Conversion Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Avg Deal Size
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamMembers.map((member) => {
                  const wonRank = rankedByWon.findIndex(m => m.userId === member.userId) + 1;
                  const pipelineRank = rankedByPipeline.findIndex(m => m.userId === member.userId) + 1;
                  const conversionRank = rankedByConversion.findIndex(m => m.userId === member.userId) + 1;
                  
                  // Safe access to metrics with defaults
                  const metrics = member.metrics || {
                    newLeads: { count: 0, value: 0 },
                    wonLeads: { count: 0, value: 0 },
                    lostLeads: { count: 0, value: 0 },
                    activePipeline: { count: 0, value: 0, weightedValue: 0 },
                    conversionRate: 0,
                    avgDealSize: 0,
                  };
                  
                  return (
                    <tr key={member.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-600 font-semibold">
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{metrics.newLeads.count}</div>
                        <div className="text-xs text-gray-500">
                          {formatCurrency(metrics.newLeads.value, currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div>
                            <div className="text-sm font-semibold text-green-600">
                              {metrics.wonLeads.count}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatCurrency(metrics.wonLeads.value, currency)}
                            </div>
                          </div>
                          {wonRank === 1 && (
                            <TrophyIcon className="h-4 w-4 text-yellow-500" title="Top performer" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{metrics.lostLeads.count}</div>
                        <div className="text-xs text-gray-500">
                          {formatCurrency(metrics.lostLeads.value, currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {metrics.activePipeline.count}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatCurrency(metrics.activePipeline.value, currency)}
                        </div>
                        <div className="text-xs text-indigo-600 font-medium">
                          W: {formatCurrency(metrics.activePipeline.weightedValue, currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-semibold text-gray-900">
                            {metrics.conversionRate.toFixed(1)}%
                          </div>
                          {conversionRank === 1 && (
                            <SparklesIcon className="h-4 w-4 text-indigo-500" title="Best conversion rate" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(metrics.avgDealSize, currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {!isLoading && !error && teamMembers.length === 0 && allTeamMembers.length > 0 && (
          <div className="premium-card p-6 text-center">
            <p className="text-gray-600 font-medium">No team members selected</p>
            <p className="text-gray-500 text-sm mt-2">
              Select team members from the filter above to see their performance comparison.
            </p>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default TeamView;
